import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GetPrint from './GetPrint';
import { Card, CardContent } from '../components/ui/card';
import { FiLoader } from 'react-icons/fi';
import IconWrapper from '../components/print-steps/IconWrapper';
import { WhatsAppDocumentService } from '../services/WhatsAppDocumentService';
import toast from 'react-hot-toast';
import usePrintStore from '../store/printStore';
import { processFile } from '../utils/fileProcessing';
import { Document } from '../types/documentTypes';
import { getShopInfoByUuid } from '../lib/supabase/api/get-shop-info';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import FullScreenLoader from '../components/ui/FullScreenLoader';

const ShopWhatsAppDirectPrint: React.FC = () => {
  const { sessionId, shopId } = useParams<{ sessionId: string; shopId: string }>();
  const { addDocument, setSelectedShop } = usePrintStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [verifiedShopId, setVerifiedShopId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [documentsProcessed, setDocumentsProcessed] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Memoize the document processing function
  const processDocument = useCallback(async (file: File) => {
    try {
      let fileToProcess = file;
      if (!file.type || file.type === 'application/octet-stream') {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (extension === 'jpg' || extension === 'jpeg') {
          fileToProcess = new File([file], file.name, { type: 'image/jpeg' });
        } else if (extension === 'png') {
          fileToProcess = new File([file], file.name, { type: 'image/png' });
        } else if (extension === 'pdf') {
          fileToProcess = new File([file], file.name, { type: 'application/pdf' });
        }
      }
      
      const processedFile = await processFile(fileToProcess);
      
      const newDocument: Document = {
        id: `whatsapp-${sessionId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: processedFile.file.name,
        url: processedFile.url,
        file: processedFile.file,
        pageCount: processedFile.pageCount,
        source: 'whatsapp'
      };
      
      addDocument(newDocument);
      return true;
    } catch (error) {
      console.error('Error processing file:', error);
      return false;
    }
  }, [sessionId, addDocument]);

  // Verify shop status
  useEffect(() => {
    const verifyShop = async () => {
      if (!shopId) {
        console.error('Shop ID is missing from URL');
        setErrorMessage('Shop ID is missing from the link.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        console.log(`Verifying shop ID: ${shopId} from QR code`);
        
        const shopData = await getShopInfoByUuid(shopId);
        
        if (!shopData) {
          console.log('Shop not found, redirecting to registration');
          navigate('/shop-registration', { state: { shopId } });
          return;
        }

        console.log(`Shop online status: ${shopData.is_online}`);
        
        if (!shopData.is_online) {
          console.log('Shop is offline, showing offline dialog');
          setErrorMessage(`${shopData.shop_name} is currently offline. Please try again later.`);
          setIsLoading(false);
          return;
        }

        // Shop is online, set the selected shop in the store
        console.log(`Shop is online, setting shop ID: ${shopId}`);
        setSelectedShop(shopId);
        setVerifiedShopId(shopId);
        setIsLoading(false);
      } catch (error) {
        console.error('Error verifying shop:', error);
        setErrorMessage('Failed to verify shop. Please try again.');
        setIsLoading(false);
      }
    };
    
    verifyShop();
  }, [shopId, setSelectedShop, navigate]);

  // Process WhatsApp documents
  useEffect(() => {
    if (!sessionId || documentsProcessed || !verifiedShopId) return;
    
    let mounted = true;
    console.log(`User arriving with WhatsApp session ID: ${sessionId}`);
    
    // Create new AbortController for this effect instance
    abortControllerRef.current = new AbortController();
    
    const downloadWhatsAppDocuments = async () => {
      if (!mounted) return;
      setIsLoading(true);
      
      try {
        const result = await WhatsAppDocumentService.downloadWhatsAppDocuments(
          sessionId,
          abortControllerRef.current?.signal
        );
        
        if (!result.success || !mounted) {
          if (mounted) {
            toast.error('No documents found or download failed', {
              position: 'top-center',
              duration: 3000
            });
            setDocumentsProcessed(true);
          }
          return;
        }
        
        console.log(`Downloaded ${result.downloadedCount} WhatsApp documents`);
        let processedCount = 0;
        
        for (const file of result.files) {
          if (!mounted) break;
          const success = await processDocument(file);
          if (success) processedCount++;
        }
        
        if (mounted) {
          setDocumentsProcessed(true);
          
          if (processedCount > 0) {
            toast.success(`${processedCount} document${processedCount > 1 ? 's' : ''} loaded from WhatsApp`, {
              position: 'top-center',
              duration: 3000,
              id: 'whatsapp-documents-loaded'
            });
          }
          
          if (result.downloadedCount - processedCount > 0) {
            toast.error(`${result.downloadedCount - processedCount} document${result.downloadedCount - processedCount > 1 ? 's' : ''} failed to load`, {
              position: 'top-center',
              duration: 3000,
              id: 'whatsapp-documents-failed'
            });
          }
        }
      } catch (error) {
        if (mounted) {
          console.error('Error processing WhatsApp documents:', error);
          toast.error('Failed to load WhatsApp documents', {
            position: 'top-center',
            id: 'whatsapp-documents-error'
          });
          setDocumentsProcessed(true);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    
    downloadWhatsAppDocuments();
    
    return () => {
      mounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort(); // Cancel any ongoing requests
        abortControllerRef.current = null;
      }
    };
  }, [sessionId, verifiedShopId, addDocument, documentsProcessed, processDocument]);

  const handleCloseErrorDialog = () => {
    navigate('/');
  };

  // Show loading state while checking shop existence or processing documents
  if (isLoading) {
    return <FullScreenLoader message="Processing your request..." />;
  }

  // Show error dialog if there's any issue
  if (errorMessage) {
    return (
      <Dialog open={!!errorMessage} onOpenChange={handleCloseErrorDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Unable to Process Request</DialogTitle>
            <DialogDescription>
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>
              Return to Home
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      {isLoading && (
        <div className="fixed top-20 left-0 right-0 z-50 flex justify-center px-4">
          <Card className="bg-blue-50 border-blue-200 max-w-md shadow-lg border">
            <CardContent className="p-4">
              <div className="flex items-center">
                <IconWrapper icon={FiLoader} className="text-blue-600 mr-3 text-lg animate-spin" />
                <div>
                  <p className="text-blue-800 text-sm">
                    Loading your documents...
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <GetPrint 
        directShopId={verifiedShopId} 
        whatsAppSession={sessionId} 
      />
    </>
  );
};

export default ShopWhatsAppDirectPrint;
